import React, { createContext, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthHeaders, PATH_INFO } from "../pages/util/helper";

export const defaultDevice = {
  id: '',
  company_id: '',
  name: '',
  location: '',
  api_key: '',
  created_at: '',
  updated_at: '',
  company: { company_name: '' }
}

export type DeviceResponseType = {
  id: string
  company_id: string
  name: string
  location: string
  api_key: string
  created_at: string
  updated_at: string
  company: { company_name: string }
}

export type DeviceRequestType = {
  company_id: string
  location: string
  name: string
}

type DevicePagination = {
  page: number
  pageSize: number
  total: number
}

type DeviceListOptions = { page?: number }

const defaultDevicePagination: DevicePagination = {
  page: 1,
  pageSize: 6,
  total: 0,
}

export const DeviceContext = createContext<{
  deviceList: DeviceResponseType[]
  device: DeviceResponseType
  devicePagination: DevicePagination
}>({
  deviceList: [],
  device: defaultDevice,
  devicePagination: defaultDevicePagination,
})

export const UpdateDeviceContext = createContext<{
  refreshDeviceList: (id: string, options?: DeviceListOptions) => Promise<void>
  refreshDevice: (id: string) => Promise<void>
  registerDevice: (value: DeviceRequestType) => Promise<boolean>
  updateDevice: ({ location, name, device_id }: { location: string; name: string; device_id: string }) => Promise<boolean>
  regenerateDeviceKey: (device_id: string) => Promise<boolean>
  deleteDevice: (device_id: string) => Promise<boolean>
}>({
  refreshDeviceList: async () => { },
  refreshDevice: async () => { },
  registerDevice: async () => false,
  updateDevice: async () => false,
  regenerateDeviceKey: async () => false,
  deleteDevice: async () => false,
})

const toPositiveInteger = (value: number, fallback: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback
  }

  return Math.trunc(value)
}

export function useDeviceContext() {
  const [deviceList, setDeviceList] = useState<DeviceResponseType[]>([])
  const [device, setDevice] = useState<DeviceResponseType>(defaultDevice)
  const [devicePagination, setDevicePagination] = useState<DevicePagination>({ ...defaultDevicePagination })
  const navigate = useNavigate()

  // 企業IDから企業のデバイス一覧を取得
  const refreshDeviceList = useCallback(async (id: string, options?: DeviceListOptions) => {
    if (!id) {
      setDeviceList([])
      setDevicePagination({ ...defaultDevicePagination })
      return
    }

    try {
      const page = toPositiveInteger(options?.page ?? defaultDevicePagination.page, defaultDevicePagination.page)
      const pageSize = defaultDevicePagination.pageSize

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices/company/${id}?${params.toString()}`, {
        headers
      })

      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await res.json();

      const devices = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : []

      setDeviceList(devices)

      const pagination = data?.pagination

      if (pagination && typeof pagination === 'object') {
        setDevicePagination({
          page: typeof pagination.page === 'number' ? toPositiveInteger(pagination.page, page) : page,
          pageSize: defaultDevicePagination.pageSize,
          total: typeof pagination.total === 'number' && pagination.total >= 0 ? Math.trunc(pagination.total) : devices.length,
        });
      } else {
        setDevicePagination({
          page,
          pageSize: defaultDevicePagination.pageSize,
          total: devices.length,
        })
      }
    } catch (error) {
      console.error("Error fetching devices.", error)
    }
  }, [navigate])

  // デバイスIDからデバイスの詳細取得
  const refreshDevice = useCallback(async (id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices/${id}`, {
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok")
      }
      const data = await res.json()
      setDevice(data)
    } catch (error) {
      console.error("Error fetching device", error)
    }
  }, [navigate])

  // デバイスの新規登録
  const registerDevice = useCallback(async (value: DeviceRequestType) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return false
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices`, {
        method: 'POST',
        headers,
        body: JSON.stringify(value)
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        return false
      }
      return true
    } catch (error) {
      console.error("Error fetching device", error)
      return false
    }
  }, [navigate])

  // デバイスの更新
  const updateDevice = useCallback(async ({ location, name, device_id }: { location: string; name: string; device_id: string }) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return false
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices/${device_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ location, name })
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        return false
      }
      return true
    } catch (error) {
      console.error("Error fetching device", error)
      return false
    }
  }, [navigate])

  // デバイスのAPIキー再発行
  const regenerateDeviceKey = useCallback(async (device_id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return false
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices/${device_id}/regenerate-key`, {
        method: 'POST',
        headers
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        return false
      }
      return true
    } catch (error) {
      console.error("Error fetching device", error)
      return false
    }
  }, [navigate])

  // デバイスの削除
  const deleteDevice = useCallback(async (device_id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return false
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices/${device_id}`, {
        method: 'DELETE',
        headers
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        return false
      }
      return true
    } catch (error) {
      console.error("Error fetching device", error)
      return false
    }
  }, [navigate])

  return {
    deviceList,
    device,
    devicePagination,
    refreshDeviceList,
    refreshDevice,
    registerDevice,
    updateDevice,
    regenerateDeviceKey,
    deleteDevice,
  };
}

export function DeviceProvicer({ children }: { children: React.ReactNode }) {
  const { deviceList, device, devicePagination, refreshDeviceList, refreshDevice, registerDevice, updateDevice, regenerateDeviceKey, deleteDevice } = useDeviceContext()

  return (
    <UpdateDeviceContext.Provider value={{ refreshDeviceList, refreshDevice, registerDevice, updateDevice, regenerateDeviceKey, deleteDevice }}>
      <DeviceContext.Provider value={{ deviceList, device, devicePagination }}>
        {children}
      </DeviceContext.Provider>
    </UpdateDeviceContext.Provider>
  )
}